import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import flatMap from 'lodash/flatMap';
import uniqBy from 'lodash/uniqBy';
// import { Responsive } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { CloseIcon, SettingsIcon } from 'base/components/Icon/index';
import theme, { textRegular } from 'theme/theme';
import MainTitle from 'base/components/MainTitle/index';
import Flex from 'base/components/Flex/Flex';
import ButtonGroup from 'base/components/ButtonGroup';
import Card from 'base/components/Card/index';
import LargeIconButton from 'base/components/LargeIconButton/index';
import Toolbox from '../../components/Toolbox/Toolbox';
import WidthProvider from './WidthProvider';
import Responsive from './ResponsiveReactGridLayout';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const RemoveButtonBase = (props) => (
  <div {...props}>
    <CloseIcon size="22px" color={theme.colors.light} style={{ top: 0 }} />
  </div>
);
const RemoveButton = styled(RemoveButtonBase)`
  position: absolute;
  top: -20px;
  right: -20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(60,61,53,0.8);
  &:hover {
    cursor: pointer;
    background: ${theme.colors.primary};
  }
`;

const WidgetTitle = styled.h3`
  display: flex;
  flex-shrink: 0;
  margin: 0;
  padding: 15px 20px;
  border-bottom: 1px solid ${theme.colors.light};
  color: ${theme.colors.primary};
  ${textRegular};
  font-size: ${theme.fontSizes.large};
`;

const WidgetContent = styled(Flex)`
  flex-direction: column;
  overflow: auto;
  flex: 0 100%;
`;

const Widget = ({
  children, title, ...other
}) => (
  <Card flexDirection="column" {...other}>
    <WidgetTitle>
      {title}
    </WidgetTitle>
    <WidgetContent>
      {children}
    </WidgetContent>
  </Card>
);
Widget.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

const DashboardWrapper = styled.div`
  padding: 25px 25px;
  background: ${theme.colors.light};
  color: ${theme.colors.dark};
  
  @media (min-width: ${theme.breakpoints.desktop}) {
    padding: 45px 65px;
  }
  
  .react-grid-layout {
    margin: -40px;
  }
  
  .react-grid-item.react-grid-placeholder {
    opacity: 0.2;
    background: ${theme.colors.dark};
    border: 2px dashed ${theme.colors.darker};
  }
  
  .react-draggable {
    &:hover {
      cursor: move;
    }
  }
  
  .react-resizable-handle {
    width: 20px;
    height: 20px;
    background-size: 100% 100%;
  }
`;

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: this.props.isAdminMode || false,
      currentBreakpoint: 'lg',
      compactType: 'vertical',
      mounted: false,
      layouts: this.props.layouts,
      toolbox: this.props.toolboxWidgets,
    };
  }

  componentDidMount() {
    this.setState({mounted: true}); // eslint-disable-line
  }

  onBreakpointChange = (breakpoint, cols) => {
    this.setState((prevState) => ({
      currentBreakpoint: breakpoint,
      toolbox: {
        ...prevState.toolbox,
        [breakpoint]:
        prevState.toolbox[breakpoint]
        || prevState.toolbox[prevState.currentBreakpoint]
        || [],
      },
    }));
    this.props.onBreakpointChange(breakpoint, cols);
  };

  onTakeItem = (item) => {
    this.setState((prevState) => ({
      toolbox: {
        ...prevState.toolbox,
        [prevState.currentBreakpoint]: prevState.toolbox[
          prevState.currentBreakpoint
        ].filter(({ i }) => i !== item.i),
      },
      layouts: {
        ...prevState.layouts,
        [prevState.currentBreakpoint]: [
          ...prevState.layouts[prevState.currentBreakpoint],
          item,
        ],
      },
    }));
  };

  onPutItem = (item) => {
    this.setState((prevState) => ({
      toolbox: {
        ...prevState.toolbox,
        [prevState.currentBreakpoint]: [
          ...(prevState.toolbox[prevState.currentBreakpoint] || []),
          item,
        ],
      },
      layouts: {
        ...prevState.layouts,
        [prevState.currentBreakpoint]: prevState.layouts[
          prevState.currentBreakpoint
        ].filter(({ i }) => i !== item.i),
      },
    }));
  };

  onLayoutChange = (layout, layouts) => {
    this.props.onLayoutChange(layout, layouts);
    this.setState({ layouts });
  };

  onStartEditing = () => {
    this.props.onStartEditing(this.state.layouts);
    this.setState({ isEditing: true });
  };

  onStopEditing = () => {
    this.props.onStopEditing(this.state.layouts);
    this.setState({ isEditing: false });
  };

  render() {
    const {
      isEditing,
      toolbox,
      currentBreakpoint,
      mounted,
      compactType,
      layouts,
      onStopEditing,
    } = this.state;
    const {
      widgets, isAdminMode, dashboardId, onBreakpointChange, ...other
    } = this.props;

    const dashboardContent = uniqBy(
      flatMap(
        layouts,
        (layout) => layout.map((l) => {
          const widget = widgets.find((w) => w.id === l.i);
          if (!widget) {
            return null;
          }
          const WidgetComponent = widget.component;
          return (
            <Widget key={l.i} title={widget.title}>
              {this.state.isEditing
                    && <RemoveButton onClick={() => this.onPutItem(l)} />}
              <WidgetComponent
                {...widget.componentProps}
                isAdminMode={isAdminMode}
                dashboardId={dashboardId}
              />
            </Widget>
          );
        })
        ,
      ).filter((widget) => widget !== null),
      'key',
    );

    return (
      <div style={{ width: '100%' }}>
        {isEditing
          && (
          <Toolbox
            items={toolbox[currentBreakpoint] || []}
            onTakeItem={this.onTakeItem}
            onClose={this.onStopEditing}
            widgets={widgets}
            isAdminMode={isAdminMode}
          />
          )}

        <DashboardWrapper>
          {!isAdminMode && (
            <Flex style={{ marginBottom: '35px' }}>
              <MainTitle>
                    Dashboard
              </MainTitle>
              {!isEditing && (
              <ButtonGroup pullRight>
                <LargeIconButton
                  onClick={this.onStartEditing}
                >
                          Customise Dashboard
                  {' '}
                  <SettingsIcon size="25px" />
                </LargeIconButton>
              </ButtonGroup>
              )}
            </Flex>
          )}

          <ResponsiveReactGridLayout
            {...other}
            layouts={layouts}
            onLayoutChange={this.onLayoutChange}
            onBreakpointChange={this.onBreakpointChange}
            breakpoints={{ lg: 1100, sm: 768, xxs: 0 }}
            cols={{ lg: 6, sm: 2, xxs: 1 }}
            measureBeforeMount
            useCSSTransforms={mounted}
            compactType={compactType}
            preventCollision={!compactType}
            margin={[40, 40]}
            isDraggable={isEditing}
            isResizable={isEditing}
            onStopEditing={onStopEditing}
          >
            {dashboardContent}
          </ResponsiveReactGridLayout>

        </DashboardWrapper>

      </div>
    );
  }
}

Dashboard.propTypes = {
  layouts: PropTypes.objectOf(PropTypes.any),
  toolboxWidgets: PropTypes.objectOf(PropTypes.any),
  onLayoutChange: PropTypes.func,
  widgets: PropTypes.arrayOf(PropTypes.object).isRequired,
  onStopEditing: PropTypes.func,
  onStartEditing: PropTypes.func,
  isAdminMode: PropTypes.bool,
  dashboardId: PropTypes.number,
  className: PropTypes.string,
  rowHeight: PropTypes.number,
  cols: PropTypes.objectOf(PropTypes.any),
  onBreakpointChange: PropTypes.func,
};
Dashboard.defaultProps = {
  className: 'layout',
  rowHeight: 30,
  cols: {
    lg: 6, md: 6, sm: 6, xs: 1, xxs: 1,
  },
  layouts: {},
  toolboxWidgets: [],
  onLayoutChange: () => {
  },
  onStopEditing: () => {
  },
  onStartEditing: () => {
  },
  isAdminMode: false,
  dashboardId: null,
  onBreakpointChange: () => {
  },
};

export default Dashboard;
